'use client';

import DOMPurify from 'isomorphic-dompurify';
import { Close } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography
} from '@mui/material';
import { useMemo } from 'react';

import { getEposMembershipProgrammeTcContent } from '@/constants/membership-programme-tc-content';
import { useQueryConfigs } from '@/query/hooks/use-query-configs';

const TITLE = 'Membership Programme Terms';

interface MembershipProgrammeTermsDialogProps {
  open: boolean;
  onClose: () => void;
  merchantName?: string;
}

const MembershipProgrammeTermsDialog: React.FC<MembershipProgrammeTermsDialogProps> = ({
  open,
  onClose,
  merchantName
}) => {
  const { data: configs } = useQueryConfigs();

  const content = useMemo(() => {
    const rawContent = getEposMembershipProgrammeTcContent(configs?.countryCode ?? 'SG', merchantName);
    return DOMPurify.sanitize(rawContent, { ADD_ATTR: ['target'] });
  }, [configs?.countryCode, merchantName]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      sx={{
        '@media (max-width: 899.95px)': {
          '& .MuiDialog-container': {
            alignItems: 'flex-end'
          },
          '& .MuiDialog-paper': {
            borderRadius: '16px 16px 0 0',
            margin: 0,
            width: '100%',
            maxWidth: '100%'
          }
        }
      }}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          height: '80vh',
          maxHeight: '80vh'
        }
      }}
    >
      <div className='flex items-center justify-between px-4 py-3 md:px-5 md:py-4'>
        <div>
          <Typography component='h2' variant='h6' className='!font-bold'>
            {TITLE}
          </Typography>
          {merchantName && (
            <Typography variant='body2' color='text.secondary'>
              {merchantName}
            </Typography>
          )}
        </div>
        <IconButton onClick={onClose} size='small'>
          <Close />
        </IconButton>
      </div>
      <DialogContent dividers sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Box
          dangerouslySetInnerHTML={{ __html: content }}
          sx={{
            '& h3': {
              fontSize: 16,
              fontWeight: 600,
              marginTop: 2,
              marginBottom: 1
            },
            '& h4': {
              fontSize: 16,
              fontWeight: 600,
              marginTop: 1.5,
              marginBottom: 0.75
            },
            '& p': {
              fontSize: 14,
              lineHeight: 1.6,
              marginBottom: 1
            },
            '& ul, & ol': {
              paddingLeft: 2.5,
              marginBottom: 1,
              fontSize: 14
            },
            // Tailwind preflight resets list-style to none; restore the marker styles driven by the <ol type> attribute
            '& ol[type="a"]': {
              listStyleType: 'lower-alpha'
            },
            '& ol[type="i"]': {
              listStyleType: 'lower-roman'
            },
            '& li': {
              marginBottom: 0.5,
              lineHeight: 1.5
            },
            '& strong': {
              fontWeight: 600
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default MembershipProgrammeTermsDialog;
